import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, MapPin, Clock, Car, Navigation, CheckCircle, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import AddParkingDialog from './AddParkingDialog';

// Lavender theme tokens
const THEME = {
  primary: '#C4B5FD',
  light: '#EDE9FE',
  lighter: '#F5F3FF',
  dark: '#5B21B6',
  icon: '#8B5CF6',
  border: '#DDD6FE',
  surface: '#FAFAFD',
};

const ActiveParkingsTab = ({ vehicles }) => {
  const { isDemoMode, getParkingLocations, deleteParkingLocation } = useDemo();
  const [parkings, setParkings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchActiveParkings();
  }, [isDemoMode]);

  const fetchActiveParkings = async () => {
    // In demo mode, use demo data instead of API
    if (isDemoMode) {
      const demoParking = getParkingLocations();
      setParkings(demoParking);
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/parking/active');
      setParkings(response.data);
    } catch (error) {
      console.error('Failed to fetch active parkings:', error);
      toast.error('Failed to load active parkings');
    } finally {
      setLoading(false);
    }
  };

  const handleEndParking = async (id) => {
    // In demo mode, just remove from state
    if (isDemoMode) {
      deleteParkingLocation(id);
      setParkings(prev => prev.filter(p => p.id !== id));
      toast.success('Parking ended');
      return;
    }
    
    try {
      await API.put(`/parking/${id}/end`);
      toast.success('Parking ended');
      fetchActiveParkings();
    } catch (error) {
      toast.error('Failed to end parking');
    }
  };

  const handleDelete = async (id) => {
    // In demo mode, just remove from state
    if (isDemoMode) {
      deleteParkingLocation(id);
      setParkings(prev => prev.filter(p => p.id !== id));
      toast.success('Parking deleted');
      return;
    }
    
    try {
      await API.delete(`/parking/${id}`);
      toast.success('Parking deleted');
      fetchActiveParkings();
    } catch (error) {
      toast.error('Failed to delete parking');
    }
  };

  const handleDirections = (parking) => {
    if (parking.latitude && parking.longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${parking.latitude},${parking.longitude}`;
      window.open(url, '_blank');
    } else if (parking.address) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(parking.address)}`;
      window.open(url, '_blank');
    } else {
      toast.error('No location information available');
    }
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : null;
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h3 
            className="text-xl font-semibold"
            style={{ color: THEME.dark }}
          >
            Active Parkings
          </h3>
          <p className="text-sm text-gray-500 mt-1">Where you&apos;re currently parked</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.primary,
            color: THEME.dark,
          }}
          data-testid="add-parking-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          I Just Parked
        </Button>
      </div>

      {parkings.length === 0 ? (
        <div 
          className="text-center py-8 rounded-2xl shadow-sm"
          style={{ 
            backgroundColor: THEME.lighter,
          }}
        >
          <div 
            className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3"
            style={{ backgroundColor: THEME.light }}
          >
            <Car className="h-6 w-6" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-base font-semibold text-gray-900 mb-1">No active parking saved</h4>
          <p className="text-sm text-gray-500 max-w-xs mx-auto">
            Save where you parked so you can find your car later.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {parkings.map((parking) => (
            <Card 
              key={parking.id} 
              className="overflow-hidden hover:shadow-lg transition-all duration-200"
              style={{ 
                borderColor: THEME.border,
                borderWidth: '1px',
                borderLeftWidth: '4px',
                borderLeftColor: parking.is_primary ? THEME.icon : THEME.primary,
                backgroundColor: parking.is_primary ? THEME.lighter : '#FFFFFF',
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <h4 className="font-semibold text-gray-900">{parking.title}</h4>
                      {parking.is_primary && (
                        <Badge 
                          style={{ 
                            backgroundColor: THEME.icon, 
                            color: 'white' 
                          }}
                        >
                          Primary
                        </Badge>
                      )}
                      <Badge 
                        variant="outline" 
                        className="font-medium"
                        style={{ 
                          borderColor: '#D1FAE5',
                          color: '#166534',
                          backgroundColor: '#F0FDF4',
                        }}
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTimeAgo(parking.parked_at)}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600">
                      {/* Vehicle */}
                      {parking.vehicle_id && getVehicleName(parking.vehicle_id) && (
                        <div className="flex items-center gap-2">
                          <Car className="h-4 w-4 text-gray-400" />
                          {getVehicleName(parking.vehicle_id)}
                        </div>
                      )}
                      
                      {/* Location */}
                      {(parking.address || parking.place_name) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {parking.place_name || parking.address}
                        </div>
                      )}
                      
                      {/* Where I Parked */}
                      {parking.where_i_parked && (
                        <div 
                          className="mt-3 p-3 rounded-lg"
                          style={{ 
                            backgroundColor: THEME.light,
                            color: THEME.dark,
                          }}
                        >
                          <strong>Where:</strong> {parking.where_i_parked}
                        </div>
                      )}
                      
                      {/* Structured parking info */}
                      {(parking.garage_name || parking.level_floor || parking.spot_number) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {parking.garage_name && (
                            <Badge 
                              style={{ 
                                backgroundColor: THEME.light,
                                color: THEME.dark,
                              }}
                            >
                              {parking.garage_name}
                            </Badge>
                          )}
                          {parking.level_floor && (
                            <Badge 
                              variant="outline" 
                              style={{ borderColor: THEME.border }}
                            >
                              Level: {parking.level_floor}
                            </Badge>
                          )}
                          {parking.section_zone && (
                            <Badge 
                              variant="outline"
                              style={{ borderColor: THEME.border }}
                            >
                              Section: {parking.section_zone}
                            </Badge>
                          )}
                          {parking.spot_number && (
                            <Badge 
                              variant="outline"
                              style={{ borderColor: THEME.border }}
                            >
                              Spot: {parking.spot_number}
                            </Badge>
                          )}
                          {parking.row_aisle && (
                            <Badge 
                              variant="outline"
                              style={{ borderColor: THEME.border }}
                            >
                              Row: {parking.row_aisle}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Landmarks */}
                      {parking.landmarks && (
                        <p className="text-gray-500 italic mt-2">Landmarks: {parking.landmarks}</p>
                      )}
                      
                      {/* Payment info */}
                      {(parking.paid_until || parking.amount_paid) && (
                        <div className="flex items-center gap-3 mt-3">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          {parking.amount_paid && (
                            <span className="text-green-600 font-medium">Paid ${parking.amount_paid}</span>
                          )}
                          {parking.paid_until && (
                            <Badge 
                              variant="outline" 
                              style={{ 
                                borderColor: '#FED7AA',
                                color: '#9A3412',
                                backgroundColor: '#FFF7ED',
                              }}
                            >
                              Meter until {new Date(parking.paid_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Notes */}
                      {parking.notes && (
                        <p className="text-gray-500 text-xs mt-2">{parking.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => handleDirections(parking)}
                      className="bg-blue-500 hover:bg-blue-600 text-white shadow-sm"
                      size="sm"
                      data-testid={`directions-btn-${parking.id}`}
                    >
                      <Navigation className="h-4 w-4 mr-1" />
                      Directions
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEndParking(parking.id)}
                      className="border-green-300 hover:bg-green-50"
                      style={{ color: '#166534' }}
                      data-testid={`end-parking-btn-${parking.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Found It
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(parking.id)}
                      className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-parking-btn-${parking.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddParkingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vehicles={vehicles}
        onSuccess={() => {
          fetchActiveParkings();
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default ActiveParkingsTab;
